import { useEffect, useRef, useState } from 'react';
import { settleProtection } from '../lib/protection.js';
import { useSession } from '../lib/session.jsx';

/**
 * The shield reading: a ring of light around the number.
 *
 * The value is computed locally from (stored level, elapsed, context) — a pure
 * function — so this repaints every frame without a single request.
 */
const SIZE = 240, C = SIZE / 2, R = 96;

export default function ProtectionDial({ size = SIZE }) {
  const { device, hasActiveSubscription } = useSession();
  const [now, setNow] = useState(() => Date.now());
  const raf = useRef();

  useEffect(() => {
    const tick = () => { setNow(Date.now()); raf.current = requestAnimationFrame(tick); };
    raf.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf.current);
  }, []);

  if (!device) return null;

  const { level, isCharging, target, minutesToTarget } = settleProtection({
    level: device.protection_level, updatedAt: device.protection_updated_at, now,
    hasSubscription: hasActiveSubscription, deviceActive: device.is_active,
    inField: Boolean(device.checked_in_field_id),
  });

  const circ = 2 * Math.PI * R;
  const dash = circ * (level / 100);
  const targetDash = circ * (target / 100);
  const a = isCharging ? 'var(--sage)' : 'var(--clay)';
  const b = isCharging ? 'var(--clay)' : 'var(--apricot)';
  const whole = Math.floor(level), frac = Math.round((level - whole) * 10);

  return (
    <div className="relative flex flex-col items-center">
      <svg width={size} height={size} viewBox={`0 0 ${SIZE} ${SIZE}`} className="overflow-visible">
        <defs>
          <linearGradient id="shield-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0" stopColor={a} /><stop offset="1" stopColor={b} />
          </linearGradient>
          <filter id="shield-glow" x="-30%" y="-30%" width="160%" height="160%">
            <feGaussianBlur stdDeviation="6" />
          </filter>
        </defs>

        {/* ambient */}
        <circle cx={C} cy={C} r={R + 16} fill="none" stroke={a} strokeWidth="30" opacity={isCharging ? 0.16 : 0.08}
                filter="url(#shield-glow)" className={isCharging ? 'breathe' : ''} style={{ transformOrigin: `${C}px ${C}px` }} />

        {/* track */}
        <circle cx={C} cy={C} r={R} fill="none" stroke="var(--glass-3)" strokeWidth="10" />
        {/* target ghost */}
        {Math.abs(target - level) > 0.5 && (
          <circle cx={C} cy={C} r={R} fill="none" stroke={a} strokeWidth="10" strokeOpacity="0.18"
                  strokeDasharray={`${targetDash} ${circ}`} strokeLinecap="round"
                  transform={`rotate(-90 ${C} ${C})`} />
        )}
        {/* reading */}
        {level > 0.3 && (
          <circle cx={C} cy={C} r={R} fill="none" stroke="url(#shield-g)" strokeWidth="10"
                  strokeDasharray={`${dash} ${circ}`} strokeLinecap="round"
                  transform={`rotate(-90 ${C} ${C})`}
                  style={{ filter: `drop-shadow(0 0 10px ${a})` }} />
        )}
        {/* rotating dashes when charging */}
        {isCharging && (
          <circle cx={C} cy={C} r={R + 20} fill="none" stroke={a} strokeWidth="1" strokeOpacity="0.5"
                  strokeDasharray="1 7" strokeLinecap="round" className="spin-slow"
                  style={{ transformOrigin: `${C}px ${C}px`, animationDuration: '30s' }} />
        )}

        <text x={C} y={C + 8} textAnchor="middle" className="display" fill="var(--text)" style={{ fontSize: 58 }}>
          {whole}<tspan style={{ fontSize: 22 }} fill="var(--text-3)">.{frac}</tspan>
        </text>
        <text x={C} y={C + 32} textAnchor="middle" fill="var(--text-3)"
              style={{ fontSize: 10, letterSpacing: '0.18em', fontWeight: 600, fontFamily: 'var(--font-sans)' }}>
          SHIELDED
        </text>
      </svg>

      <p className="text-xs font-semibold tracking-wide mt-1 text-center"
         style={{ color: isCharging ? 'var(--sage)' : 'var(--text-2)' }}>
        {!device.is_active ? 'Device dormant'
          : isCharging
            ? `Charging → ${target}%${minutesToTarget != null ? ` · ${fmt(minutesToTarget)}` : ''}`
            : hasActiveSubscription ? 'Link held' : 'No active covenant'}
      </p>
    </div>
  );
}

function fmt(mins) {
  if (mins < 1) return `${Math.ceil(mins * 60)}s`;
  if (mins < 60) return `${Math.ceil(mins)} min`;
  return `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
}
